import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Logo } from "@/components/ui/logo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmailConfirmationModal } from "@/components/email-confirmation-modal";

// Login form schema
const loginSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" }),
  rememberMe: z.boolean().optional(),
});

const registerSchema = z
  .object({
    email: z.string().email({ message: "Please enter a valid email address" }),
    fullName: z.string().optional(),
    timezone: z.string().min(1, { message: "Please select a timezone" }),
    password: z
      .string()
      .min(6, { message: "Password must be at least 6 characters" }),
    confirmPassword: z.string(),
    agreeToTerms: z.boolean().refine(val => val === true, {
      message: "You must agree to the terms and conditions",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type LoginFormValues = z.infer<typeof loginSchema>;
type RegisterFormValues = z.infer<typeof registerSchema>;

export default function AuthPage() {
  const [activeTab, setActiveTab] = useState<"login" | "register">("login");
  const { user, loginMutation, registerMutation } = useAuth();
  const { loginWithRedirect, isLoading: auth0Loading } = useAuth0();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [googleLoading, setGoogleLoading] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState<RegisterFormValues | null>(null);
  const [confirmationCode, setConfirmationCode] = useState("");

  async function handleGoogleLogin() {
    setGoogleLoading(true);
    try {
      await loginWithRedirect({
        // @ts-ignore
        connection: "google-oauth2",
      });
    } catch (err) {
      console.error("Google login failed", err);
      toast({
        title: "Login failed",
        description: "Could not log in with Google.",
        variant: "destructive",
      });
      setGoogleLoading(false);
    }
  }

  const timezones = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "America/Anchorage", label: "Alaska Time" },
    { value: "Pacific/Honolulu", label: "Hawaii Time" },
    { value: "Europe/London", label: "GMT / UTC" },
    { value: "Europe/Paris", label: "Central European Time" },
    { value: "Asia/Tokyo", label: "Japan Time" },
    { value: "Australia/Sydney", label: "Australia Eastern Time" },
  ];

  useEffect(() => {
    if (window.location.hash === "#register") setActiveTab("register");
  }, []);
  useEffect(() => {
    if (activeTab === "register")
      window.history.replaceState(null, "", "#register");
    else window.history.replaceState(null, "", window.location.pathname);
  }, [activeTab]);
  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "", rememberMe: false },
  });
  const registerForm = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      fullName: "",
      timezone: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
  });

  function onLoginSubmit(values: LoginFormValues) {
    loginMutation.mutate(
      { email: values.email, password: values.password },
      {
        onSuccess: () => navigate("/dashboard"),
      },
    );
  }
  function onRegisterSubmit(values: RegisterFormValues) {
    // Store registration data and show email confirmation modal
    setPendingRegistration(values);
    setShowEmailConfirmation(true);
  }

  const handleEmailConfirmed = (code: string) => {
    if (pendingRegistration) {
      setConfirmationCode(code);
      // Add confirmation code to registration data
      const registrationData = {
        ...pendingRegistration,
        confirmationCode: code,
      };
      
      registerMutation.mutate(registrationData, {
        onSuccess: () => {
          setShowEmailConfirmation(false);
          setPendingRegistration(null);
          setConfirmationCode("");
          navigate("/dashboard");
        },
        onError: () => {
          // Keep modal open on registration error
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8">
        <div className="flex justify-center">
          <Logo className="h-10 w-auto" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 font-serif">
          {activeTab === "login"
            ? "Sign in to your account"
            : "Create a new account"}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {activeTab === "login" ? (
            <>
              Or{" "}
              <button
                className="font-medium text-primary hover:text-primary-500"
                onClick={() => setActiveTab("register")}
              >
                create a new account
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                className="font-medium text-primary hover:text-primary-500"
                onClick={() => setActiveTab("login")}
              >
                Sign in
              </button>
            </>
          )}
        </p>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <Tabs
            defaultValue={activeTab}
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as "login" | "register")}
          >
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Sign In</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Form {...loginForm}>
                <form
                  onSubmit={loginForm.handleSubmit(onLoginSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={loginForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email address</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="you@example.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>Password</FormLabel>
                          <div className="text-sm">
                            <button
                              type="button"
                              onClick={() => navigate("/forgot-password")}
                              className="font-medium text-primary hover:text-primary-500"
                            >
                              Forgot your password?
                            </button>
                          </div>
                        </div>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="••••••••"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={loginForm.control}
                    name="rememberMe"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Remember me</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loginMutation.isPending}
                  >
                    {loginMutation.isPending ? "Signing in..." : "Sign in"}
                  </Button>
                </form>
              </Form>

              {/* Google Login Button */}
              <div className="mt-4 flex justify-center">
                {googleLoading ? (
                  <div className="w-full text-center text-gray-600">
                    Redirecting...
                  </div>
                ) : (
                  <img
                    onClick={() => {
                      if (googleLoading) {
                        return; // disable login if loading
                      }
                      handleGoogleLogin();
                    }}
                    src="https://d2hqe043i84335.cloudfront.net/public/web_light_sq_SI%402x.png"
                    alt="Sign in with Google"
                    className="h-12 cursor-pointer hover:opacity-90 transition-opacity"
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value="register">
              <Form {...registerForm}>
                <form
                  onSubmit={registerForm.handleSubmit(onRegisterSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={registerForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email address</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="you@example.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full name (optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="John Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Timezone</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select your timezone" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {timezones.map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>
                                {tz.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="••••••••"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="••••••••"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="agreeToTerms"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            I agree to the{" "}
                            <a
                              href="/terms"
                              className="text-primary hover:text-primary-500"
                            >
                              Terms of Service
                            </a>{" "}
                            and{" "}
                            <a
                              href="/privacy"
                              className="text-primary hover:text-primary-500"
                            >
                              Privacy Policy
                            </a>
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={registerMutation.isPending}
                  >
                    {registerMutation.isPending
                      ? "Creating account..."
                      : "Create account"}
                  </Button>
                </form>
              </Form>

              {/* Google Registration Button */}
              <div className="mt-4 flex justify-center">
                {googleLoading ? (
                  <div className="w-full text-center text-gray-600">
                    Redirecting...
                  </div>
                ) : (
                  <img
                    onClick={() => {
                      if (!registerForm.getValues("agreeToTerms")) {
                        toast({
                          title: "Terms Required",
                          description:
                            "You must agree to the terms before registering with Google.",
                          variant: "destructive",
                        });
                        return;
                      }
                      if (googleLoading) {
                        return; // disable register if loading
                      }
                      handleGoogleLogin();
                    }}
                    src="https://d2hqe043i84335.cloudfront.net/public/web_light_sq_SU%402x.png"
                    alt="Sign up with Google"
                    className="h-12 cursor-pointer hover:opacity-90 transition-opacity"
                  />
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Email Confirmation Modal */}
      <EmailConfirmationModal
        open={showEmailConfirmation}
        onOpenChange={setShowEmailConfirmation}
        email={pendingRegistration?.email || ""}
        onConfirmed={handleEmailConfirmed}
      />
    </div>
  );
}
